import { apiFetch } from './client'
import type {
  CompetitionPhase,
  IndexCompetition,
  IndexHomeData,
  IndexItem,
  IndexResults,
  IndexScope,
  IndexSpace,
} from '@carouselgg/nexus/index-search'
import type { MyOrg } from './use-my-orgs'
import type { MyTeam } from './use-my-teams'

// --- API shapes (GET /search/index, GET /search/home) --------------------------

interface ApiIndexItem {
  kind: IndexItem['kind']
  id: string
  name: string
  slug: string
  logo_url: string
  color: string
  score: number
  player?: {
    username: string
    is_guest: boolean
    trust_score: number
    trust_level: string
    team_name: string
    team_role: string
  }
  team?: { org_name: string; org_slug: string; game_name: string; member_count: number }
  org?: { team_count: number; competition_count: number; related_via?: string }
  competition?: {
    org_name: string
    org_slug: string
    game_name: string
    competition_type: 'league' | 'tournament'
    format: string
    phase: CompetitionPhase
    starts_at: string | null
    ends_at: string | null
    registration_closes_at: string | null
    registered_teams: number
    max_teams: number | null
    prize_pool: number
  }
}

interface ApiIndexResponse {
  query: string
  top_hit: ApiIndexItem | null
  groups: { scope: Exclude<IndexScope, 'all'>; total: number; items: ApiIndexItem[] }[]
}

interface ApiHomeResponse {
  live: ApiIndexItem[]
  open: ApiIndexItem[]
}

// --- Mapping -------------------------------------------------------------------

const orgHref = (slug: string) => `/org/${slug}`

/**
 * Turns one API result into what the Index renders, with its link. Returns
 * null for a result missing the detail its kind needs, so one malformed row
 * can't take the whole list down.
 */
export function toIndexItem(a: ApiIndexItem): IndexItem | null {
  const base = { id: a.id, name: a.name, logoUrl: a.logo_url || null, color: a.color || undefined, score: a.score }
  switch (a.kind) {
    case 'player':
      if (!a.player) return null
      return {
        ...base,
        kind: 'player',
        href: `/u/${a.player.username}`,
        handle: a.player.username,
        isGuest: a.player.is_guest,
        trustScore: a.player.is_guest ? null : a.player.trust_score,
        trustLevel: a.player.trust_level,
        teamName: a.player.team_name || undefined,
        teamRole: a.player.team_role || undefined,
      }
    case 'team':
      if (!a.team) return null
      return {
        ...base,
        kind: 'team',
        href: `${orgHref(a.team.org_slug)}/roster/${a.slug}`,
        orgName: a.team.org_name,
        orgHref: orgHref(a.team.org_slug),
        gameName: a.team.game_name,
        memberCount: a.team.member_count,
      }
    case 'competition': {
      const c = a.competition
      if (!c) return null
      return {
        ...base,
        kind: 'competition',
        href: `${orgHref(c.org_slug)}/competitions/${a.slug}`,
        orgName: c.org_name,
        gameName: c.game_name,
        competitionType: c.competition_type,
        format: c.format,
        phase: c.phase,
        startsAt: c.starts_at,
        endsAt: c.ends_at,
        registrationClosesAt: c.registration_closes_at,
        registeredTeams: c.registered_teams,
        maxTeams: c.max_teams,
        prizePool: c.prize_pool,
      }
    }
    default:
      if (!a.org) return null
      return {
        ...base,
        kind: a.kind,
        href: orgHref(a.slug),
        teamCount: a.org.team_count,
        competitionCount: a.org.competition_count,
        relatedVia: a.org.related_via || undefined,
      }
  }
}

function items(list: ApiIndexItem[]): IndexItem[] {
  return list.map(toIndexItem).filter((it): it is IndexItem => it !== null)
}

export function toIndexResults(res: ApiIndexResponse): IndexResults {
  return {
    query: res.query,
    topHit: res.top_hit ? toIndexItem(res.top_hit) : null,
    groups: res.groups.map((g) => ({ scope: g.scope, total: g.total, items: items(g.items) })),
  }
}

export async function fetchIndex(query: string, scope: IndexScope, signal?: AbortSignal): Promise<IndexResults> {
  const params = new URLSearchParams({ q: query, scope })
  const res = await apiFetch<ApiIndexResponse>(`/search/index?${params}`, { signal })
  return toIndexResults(res)
}

export async function fetchIndexHome(): Promise<Pick<IndexHomeData, 'live' | 'open'>> {
  const res = await apiFetch<ApiHomeResponse>('/search/home')
  const competitions = (list: ApiIndexItem[]) =>
    items(list).filter((it): it is IndexCompetition => it.kind === 'competition')
  return { live: competitions(res.live), open: competitions(res.open) }
}

// --- Your spaces ---------------------------------------------------------------

const ORG_KIND_LABEL: Record<string, string> = { league: 'League', university: 'University' }

/** The viewer's clubs, leagues and teams, for the Index's opening screen. */
export function spacesFrom(orgs: MyOrg[], teams: MyTeam[], limit = 5): IndexSpace[] {
  const spaces: IndexSpace[] = [
    ...orgs.map((o) => ({
      id: `org:${o.id}`,
      name: o.name,
      href: orgHref(o.slug),
      kindLabel: ORG_KIND_LABEL[o.type] ?? 'Club',
      roleLabel: o.role,
      logoUrl: o.logoUrl || null,
      color: o.brandColor,
    })),
    ...teams.map((t) => ({
      id: `team:${t.id}`,
      name: t.name,
      href: `${orgHref(t.orgSlug)}/roster/${t.slug}`,
      kindLabel: 'Team',
      roleLabel: t.role,
      logoUrl: t.logoUrl || null,
      color: t.primaryColor,
    })),
  ]
  return spaces.slice(0, limit)
}

// --- Recent searches -----------------------------------------------------------
// A per-viewer convenience, so browser storage is the right home. Storage can
// be missing or throw (private windows, blocked site data); the Index works
// without it.

export const RECENT_KEY = 'carousel_index_recent'
const RECENT_LIMIT = 6

export function loadRecent(): string[] {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(RECENT_KEY) ?? '[]')
    return Array.isArray(parsed) ? parsed.filter((q): q is string => typeof q === 'string').slice(0, RECENT_LIMIT) : []
  } catch {
    return []
  }
}

/** Puts a search at the front, without repeating it. */
export function rememberSearch(query: string, recent: string[]): string[] {
  const q = query.trim()
  if (!q) return recent
  const next = [q, ...recent.filter((r) => r.toLowerCase() !== q.toLowerCase())].slice(0, RECENT_LIMIT)
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(next))
  } catch {
    // Storage unavailable — keep it for this session only.
  }
  return next
}

export function forgetSearches(): string[] {
  try {
    localStorage.removeItem(RECENT_KEY)
  } catch {
    // Storage unavailable — nothing was saved.
  }
  return []
}
